/**
 * NETWORK-01: shared, in-memory cache for reference data (groups, agents,
 * business hours) that multiple admin pages fetch independently today —
 * e.g. GroupsAdminPages and SlaAdminPages both call GET /admin/groups on
 * every mount with no sharing between them, which is one of the sources of
 * redundant API calls this module exists to remove.
 *
 * This does not change the transport (still plain `fetch` via `api-client`)
 * and does not add polling — it only avoids re-issuing the same in-flight
 * or recently-completed request from multiple call sites at once.
 *
 * Controlled by VITE_REFERENCE_DATA_CACHE_ENABLED (default: enabled). When
 * disabled, every call bypasses the cache and hits the network directly,
 * restoring the previous per-page-fetch behavior — useful for ruling this
 * cache out during debugging without a code change.
 */
import { api } from './api-client';

const CACHE_ENABLED = import.meta.env.VITE_REFERENCE_DATA_CACHE_ENABLED !== 'false';
const TTL_MS = 60_000; // reference data changes rarely; 60s keeps pages in sync closely enough

interface CacheEntry<T> {
  value: T;
  fetchedAt: number;
  inFlight: Promise<T> | null;
}

const entries = new Map<string, CacheEntry<unknown>>();

function isFresh(entry: CacheEntry<unknown>): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

/**
 * Fetch `path` via the shared cache. Concurrent callers requesting the same
 * path while a fetch is already in flight share that single request rather
 * than each issuing their own.
 */
export async function getCached<T>(path: string): Promise<T> {
  if (!CACHE_ENABLED) return api.get<T>(path);

  const existing = entries.get(path) as CacheEntry<T> | undefined;
  if (existing) {
    if (existing.inFlight) return existing.inFlight;
    if (isFresh(existing)) return existing.value;
  }

  const promise = api.get<T>(path).then((value) => {
    entries.set(path, { value, fetchedAt: Date.now(), inFlight: null });
    return value;
  }).catch((err) => {
    entries.delete(path);
    throw err;
  });

  entries.set(path, { value: existing?.value as T, fetchedAt: existing?.fetchedAt ?? 0, inFlight: promise });
  return promise;
}

/** Force the next `getCached` call for `path` to hit the network again. */
export function invalidateCached(path: string): void {
  entries.delete(path);
}

/** Drop every cached entry — for tests, or after a bulk change that touches many resources at once. */
export function clearReferenceDataCache(): void {
  entries.clear();
}
